import React from "react";
import { alpha, Stack } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import type { TeamMember } from "../../Booking-Form/typings/team-member";
import BookingTypeSelector from "../../../components/Reports/CapacityFilter/DateAndTypeSelector/booking-type-selector.partial";
import { ScheduleColleagueSelect } from "./ScheduleColleagueSelect.partial";
import {
  FirstAidOptions,
  ScheduleFirstAidSelect,
  SelectFirstAidOptions
} from "./ScheduleFirstAidSelect.partial";
import { ScheduleMode, ScheduleModeToggle } from "./ScheduleModeToggle.partial";
import {
  ScheduleResourceAutocomplete,
  type ScheduleResourceAutocompleteOption
} from "./ScheduleResourceAutocomplete.partial";
import { ScheduleViewMode, ScheduleViewToggle } from "./ScheduleViewToggle.partial";

export const scheduleToolbarPaperSx = (theme: Theme) => ({
  p: 2,
  mb: 1.5,
  borderRadius: 2,
  border: 1,
  borderColor: "divider",
  backgroundColor:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.primary.main, 0.08)
      : alpha(theme.palette.primary.main, 0.04)
});

export type ScheduleToolbarProps = {
  scheduleMode: ScheduleMode;
  onScheduleModeChange: (mode: ScheduleMode) => void;
  scheduleViewMode: ScheduleViewMode;
  onScheduleViewModeChange: (mode: ScheduleViewMode) => void;
  /** When set, replaces the default calendar/table view toggle (e.g. dashboard week/calendar/table). */
  viewToggleSlot?: React.ReactNode;
  multiDeleteSlot?: React.ReactNode;
  isColleagueMode: boolean;
  isResourceMode: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  tagLimit: number;
  colleagues: TeamMember[];
  selectedColleagues: TeamMember[];
  scheduleColleaguesIsFetching: boolean;
  storedScheduleColleaguesIsFetching: boolean;
  preStoredColleagues: string[];
  isFirstAidSelected: boolean;
  onSelectedColleaguesChange: (colleagues: TeamMember[]) => void;
  onStoreSelectedColleagues: () => void;
  useFirstAidUserFilter?: boolean;
  firstAidOptions?: FirstAidOptions[];
  selectFirstAidOptions?: SelectFirstAidOptions;
  onSelectFirstAidOptionsChange?: (options: SelectFirstAidOptions) => void;
  resourceAvailableTypes: BookingType[];
  resourceBookingType: BookingType | null;
  onResourceBookingTypeChange: (type: BookingType | null) => void;
  onResourceFetchEnable?: () => void;
  resourceAutocompleteLoading: boolean;
  bookableBookingObjectsError: boolean;
  resourceAutocompleteOptions: ScheduleResourceAutocompleteOption[];
  selectedResource: ScheduleResourceAutocompleteOption | null;
  onSelectedResourceChange: (resource: ScheduleResourceAutocompleteOption | null) => void;
};

export function ScheduleToolbar({
  scheduleMode,
  onScheduleModeChange,
  scheduleViewMode,
  onScheduleViewModeChange,
  viewToggleSlot,
  multiDeleteSlot,
  isColleagueMode,
  isResourceMode,
  containerRef,
  tagLimit,
  colleagues,
  selectedColleagues,
  scheduleColleaguesIsFetching,
  storedScheduleColleaguesIsFetching,
  preStoredColleagues,
  isFirstAidSelected,
  onSelectedColleaguesChange,
  onStoreSelectedColleagues,
  useFirstAidUserFilter = false,
  firstAidOptions = [],
  selectFirstAidOptions = { options: [], reason: null },
  onSelectFirstAidOptionsChange,
  resourceAvailableTypes,
  resourceBookingType,
  onResourceBookingTypeChange,
  onResourceFetchEnable,
  resourceAutocompleteLoading,
  bookableBookingObjectsError,
  resourceAutocompleteOptions,
  selectedResource,
  onSelectedResourceChange
}: Readonly<ScheduleToolbarProps>) {
  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      alignItems="center"
      useFlexGap
      spacing={2}
      data-testid="schedule-mode-toggle-and-colleague-field-container"
    >
      {multiDeleteSlot}

      <ScheduleModeToggle scheduleMode={scheduleMode} setScheduleMode={onScheduleModeChange} />

      {viewToggleSlot ?? (
        <ScheduleViewToggle
          scheduleViewMode={scheduleViewMode}
          setScheduleViewMode={onScheduleViewModeChange}
        />
      )}

      {isColleagueMode && colleagues.length >= 1 && (
        <ScheduleColleagueSelect
          containerRef={containerRef}
          tagLimit={tagLimit}
          colleagues={colleagues}
          selectedColleagues={selectedColleagues}
          scheduleColleaguesIsFetching={scheduleColleaguesIsFetching}
          storedScheduleColleaguesIsFetching={storedScheduleColleaguesIsFetching}
          preStoredColleagues={preStoredColleagues}
          isFirstAidSelected={isFirstAidSelected}
          setSelectedColleagues={onSelectedColleaguesChange}
          handleStoredScheduleColleagues={onStoreSelectedColleagues}
        />
      )}

      {isColleagueMode &&
        isFirstAidSelected &&
        useFirstAidUserFilter &&
        onSelectFirstAidOptionsChange && (
          <ScheduleFirstAidSelect
            containerRef={containerRef}
            tagLimit={tagLimit}
            firstAidOptions={firstAidOptions}
            selectFirstAidOptions={selectFirstAidOptions}
            setSelectFirstAidOptions={onSelectFirstAidOptionsChange}
          />
        )}

      {isResourceMode && (
        <>
          {resourceAvailableTypes.length > 0 && (
            <BookingTypeSelector
              buttonStyle={{ fontSize: 32 }}
              iconSize="24px"
              typToggle={resourceBookingType}
              setTypToggle={nextType => {
                onResourceBookingTypeChange(nextType);
              }}
              onEnableFetch={() => {
                onResourceFetchEnable?.();
              }}
              allowedBookingTypes={resourceAvailableTypes}
            />
          )}
          <ScheduleResourceAutocomplete
            isLoading={resourceAutocompleteLoading}
            loadError={bookableBookingObjectsError}
            options={resourceAutocompleteOptions}
            value={selectedResource}
            onResourceChange={value => {
              onSelectedResourceChange(value);
            }}
          />
        </>
      )}
    </Stack>
  );
}
